import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getProject, updateProject, uploadTextFile } from '../api/project'

export const useProjectStore = defineStore('project', () => {
  // State
  const projectId = ref(0)
  const project = ref(null)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const progressStep = computed(() => {
    if (!project.value) return 0

    const statusMap = {
      'draft': 0,
      'text_uploaded': 1,
      'settings_extracted': 2,
      'setting_images_generated': 3,
      'script_generated': 4,
      'voice_generated': 5,
      'images_generated': 6,
      'videos_generated': 7,
      'completed': 8
    }

    return statusMap[project.value.status] || 0
  })

  const canExtractSettings = computed(() => {
    return project.value?.status === 'text_uploaded' || 
           project.value?.status === 'settings_extracted'
  })

  const canGenerateScript = computed(() => {
    return project.value?.status === 'setting_images_generated' ||
           project.value?.status === 'script_generated'
  })

  const canGenerateVoice = computed(() => {
    return project.value?.status === 'script_generated' ||
           project.value?.status === 'voice_generated'
  })

  const canGenerateImages = computed(() => {
    return project.value?.status === 'voice_generated' ||
           project.value?.status === 'images_generated'
  })

  const canGenerateVideos = computed(() => {
    return project.value?.status === 'images_generated' ||
           project.value?.status === 'videos_generated'
  })

  const canCompose = computed(() => {
    return project.value?.status === 'videos_generated'
  })

  // Actions
  const setProjectId = (id) => {
    projectId.value = id
  }

  const fetchProject = async () => {
    if (!projectId.value) return
    
    loading.value = true
    error.value = null
    
    try {
      const data = await getProject(projectId.value)
      project.value = data
    } catch (err) {
      error.value = err.message || '获取项目失败'
      console.error('Fetch project error:', err)
    } finally {
      loading.value = false
    }
  }

  const updateProjectStatus = async (status) => {
    if (!projectId.value) return
    
    try {
      await updateProject(projectId.value, { status })
      project.value.status = status
    } catch (err) {
      console.error('Update project status error:', err)
      throw err
    }
  }

  const updateProjectData = async (data) => {
    if (!projectId.value) return
    
    try {
      await updateProject(projectId.value, data)
      project.value = { ...project.value, ...data }
    } catch (err) {
      console.error('Update project data error:', err)
      throw err
    }
  }

  const uploadText = async (text) => {
    if (!projectId.value) throw new Error('项目ID不存在')
    
    try {
      // 创建Blob对象模拟文件上传
      const blob = new Blob([text], { type: 'text/plain' })
      const file = new File([blob], 'story.txt', { type: 'text/plain' })
      
      const response = await uploadTextFile(projectId.value, file)
      
      // 更新本地状态 - API返回的数据在response.data中
      if (response && response.data) {
        project.value = { ...project.value, ...response.data }
      }
      
      return response
    } catch (err) {
      console.error('Upload text error:', err)
      throw err
    }
  }

  return {
    // State
    projectId,
    project,
    loading,
    error,
    
    // Getters
    progressStep,
    canExtractSettings,
    canGenerateScript,
    canGenerateVoice,
    canGenerateImages,
    canGenerateVideos,
    canCompose,
    
    // Actions
    setProjectId,
    fetchProject,
    updateProjectStatus,
    updateProjectData,
    uploadText
  }
})
